"use client";

import { useCallback } from "react";
import type { Editor } from "@tiptap/react";
import { toast } from "@/hooks/useToast";
import type { BreakpointKey, ResponsiveRowStyles, BreakpointColumnConfig, BreakpointWidthConfig, BreakpointRowConfig } from "../types";
import { BREAKPOINTS } from "../types";
import {
  getAlignmentCSS,
  DEFAULT_PADDING,
} from "../utils/styleUtils";
import {
  cleanupTableWrappers,
  cleanupTableStyles,
  createTableWrapper,
  removeStyleTags,
  extractNonTableStyleTags,
  generateColumnCSS,
  generateColumnClassCSS,
  generateColumnIndexCSS,
  generateRowCSS,
  generateTableCSS,
  generateTableClassCSS,
  generateCellCSS,
  generateCellClassCSS,
  findSelectedCellIndices,
  stripDefaultTableSettings,
  stripDefaultColumnConfig,
  stripDefaultRowConfig,
  stripDefaultCellConfig,
  compactTableSettings,
  compactColumnSettings,
  getCurrentRowIndex,
  getCurrentColumnIndex,
  ensureOtherTablesHaveWrappers,
  ensureAllTablesHaveWrappers,
} from "../utils/tableUtils";
import { cellBorderWidthValue } from "../utils/tableSanitizer";
import type { TableSettings, ColumnSettings, RowSettings, CellSettings } from "../modals/UnifiedTableSettingsModal";
import { hashCode, convertToSingleQuoteAttributes, regenerateAllCSS, generateCSSForAllTables } from "./helpers/tableCSSGenerators";

const BORDER_SIDE_PROPS = ["borderTopColor", "borderRightColor", "borderBottomColor", "borderLeftColor"] as const;

/**
 * Point a cell's own border settings at a new colour, keeping its per-side
 * widths. Covers both the inline styles (what the editor shows) and the compact
 * `bd` strings inside `data-cell-settings` (what the CSS generator re-emits
 * with `!important`, and would otherwise use to undo the change).
 */
const recolorCellBorder = (cell: HTMLElement, color: string): void => {
  BORDER_SIDE_PROPS.forEach((prop) => {
    if (cell.style[prop]) cell.style[prop] = color;
  });
  if (cell.style.borderColor) cell.style.borderColor = color;

  const raw = cell.getAttribute("data-cell-settings");
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const styles = (parsed.s ?? parsed.styles) as Record<string, Record<string, unknown>> | undefined;
    if (!styles) return;

    let touched = false;
    Object.values(styles).forEach((config) => {
      // Compact format: "t:#color:width;r:#color:width;..."
      if (typeof config.bd === "string" && config.bd) {
        config.bd = config.bd
          .split(";")
          .filter(Boolean)
          .map((side) => {
            const [pos, , width] = side.split(":");
            return `${pos}:${color}:${width ?? ""}`;
          })
          .join(";");
        touched = true;
      }
      // Full format: { top: { color, width }, ... }
      const border = config.border as Record<string, { color?: string; width?: string }> | undefined;
      if (border) {
        Object.values(border).forEach((side) => {
          if (side && side.color) side.color = color;
        });
        touched = true;
      }
    });

    if (touched) cell.setAttribute("data-cell-settings", JSON.stringify(parsed));
  } catch {
    // Malformed settings – leave them alone rather than dropping the cell's styling
  }
};

interface UseTableStyleAppliersProps {
  editor: Editor | null;
  onChange?: (html: string) => void;
  isInternalChange: React.MutableRefObject<boolean>;
  tableSettings: TableSettings;
  columnSettings: ColumnSettings;
  rowSettings: RowSettings;
  cellSettings: CellSettings;
  allColumnSettings: Record<number, ColumnSettings>;
  setAllColumnSettings: React.Dispatch<React.SetStateAction<Record<number, ColumnSettings>>>;
  allRowSettings: Record<number, RowSettings>;
  setAllRowSettings: React.Dispatch<React.SetStateAction<Record<number, RowSettings>>>;
  currentTableId: string | null;
  selectedCellIndices: number[];
}

export const useTableStyleAppliers = ({
  editor,
  onChange,
  isInternalChange,
  tableSettings,
  columnSettings,
  rowSettings,
  cellSettings,
  allColumnSettings,
  setAllColumnSettings,
  allRowSettings,
  setAllRowSettings,
  currentTableId,
  selectedCellIndices,
}: UseTableStyleAppliersProps) => {
  
  // Helper to find the correct table by ID (or fallback to first table)
  const findTable = (doc: Document): HTMLTableElement | null => {
    if (currentTableId) {
      const table = doc.querySelector(`table[data-table-id="${currentTableId}"]`);
      if (table) return table as HTMLTableElement;
    }
    return doc.querySelector("table") as HTMLTableElement | null;
  };
  
  // Apply table settings
  const applyTableSettings = useCallback(() => {
    if (!editor) return;

    // Save current cell position
    const savedRowIndex = getCurrentRowIndex(editor);
    const savedColIndex = getCurrentColumnIndex(editor);

    const originalHtml = editor.getHTML();
    // Extract non-table style tags BEFORE parsing (DOMParser moves <style> to <head>)
    const { html, styleTags: preservedStyleTags } = extractNonTableStyleTags(originalHtml);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const table = findTable(doc);
    
    if (!table) return;

    // Generate unique ID for this table (or keep existing)
    const existingId = table.getAttribute("data-table-id");
    const tableId = existingId || `table-${Date.now()}`;
    table.setAttribute("data-table-id", tableId);
    
    // Store table settings as individual data attributes (like cell styles)
    // Format for breakpoint widths: "type:value:alignment:scroll:gap"
    const breakpoints: BreakpointKey[] = ["xs", "sm", "md", "lg", "xl"];
    breakpoints.forEach((bp) => {
      const config = tableSettings.tableWidths?.[bp];
      if (config) {
        // Compact format: type:value:alignment:scroll:gap
        const parts = [
          config.type || "auto",
          config.value || "",
          config.alignment || "left",
          config.horizontalScroll ? "1" : "0",
          config.rowGap !== undefined ? String(config.rowGap) : "",
        ];
        table.setAttribute(`data-width-${bp}`, parts.join(":"));
      }
    });
    
    // Store border settings as individual attributes
    table.setAttribute("data-border-color", tableSettings.tableBorderColor);
    table.setAttribute("data-border-width", String(tableSettings.tableBorderWidth));
    table.setAttribute("data-border-radius", String(tableSettings.tableBorderRadius));
    
    // Generate a hash-based class name for the table style (for CSS)
    const tableStyleHash = JSON.stringify({
      widths: tableSettings.tableWidths,
      radius: tableSettings.tableBorderRadius,
    });
    const tableClassName = `table-style-${hashCode(tableStyleHash)}`;
    
    // Remove old table-style-* classes and add the new one
    const existingClasses = table.getAttribute("class") || "";
    const classArray = existingClasses.split(" ").filter(c => c && !c.startsWith("table-style-"));
    classArray.push(tableClassName);
    table.setAttribute("class", classArray.join(" "));

    // Clean up table
    cleanupTableStyles(table as HTMLElement);

    // Apply border styles
    const hasRadius = tableSettings.tableBorderRadius > 0;
    // Use xs breakpoint row gap for inline styles (CSS will handle responsive)
    // Find the effective row gap for inline style (use first defined value for editor preview)
    // Priority: xs > sm > md > lg > xl (use first non-undefined value)
    const tableWidths = tableSettings.tableWidths || {};
    const rowGap = tableWidths.xs?.rowGap ?? tableWidths.sm?.rowGap ?? 
                   tableWidths.md?.rowGap ?? tableWidths.lg?.rowGap ?? 
                   tableWidths.xl?.rowGap ?? 0;
    
    // Always use cell borders only (no table border) to prevent double borders
    table.style.border = "none";
    table.style.borderCollapse = "separate";
    // border-spacing: horizontal vertical
    const borderSpacing = `0px ${rowGap}px`;
    table.style.borderSpacing = borderSpacing;
    // Also set as data attribute so TipTap preserves it on re-render
    table.setAttribute("data-border-spacing", borderSpacing);
    
    if (hasRadius) {
      table.style.borderRadius = `${tableSettings.tableBorderRadius}px`;
      table.style.overflow = "hidden";
    } else {
      table.style.borderRadius = "0";
      table.style.overflow = "";
    }
    
    // Apply to cells and handle corner border radius
    const rows = table.querySelectorAll("tr");
    const totalRows = rows.length;
    
    rows.forEach((row, rowIndex) => {
      const cells = row.querySelectorAll("td, th");
      const totalCols = cells.length;
      
      cells.forEach((cell, colIndex) => {
        const cellEl = cell as HTMLElement;
        const borderW = tableSettings.tableBorderWidth;
        
        const isFirstRow = rowIndex === 0;
        const isLastRow = rowIndex === totalRows - 1;
        const isFirstCol = colIndex === 0;
        const isLastCol = colIndex === totalCols - 1;
        
        // Always set data-column-index on all cells for responsive column CSS to work
        cellEl.setAttribute("data-column-index", String(colIndex));
        
        // Check if cell has custom settings - if so, only apply border-radius, not color/width
        // Check for data-cell-id, data-cell-class attribute, OR a CSS class starting with "cell-style-"
        const cellClasses = cellEl.getAttribute("class") || "";
        const hasCellStyleClass = /\bcell-style-\w+/.test(cellClasses);
        const hasCustomCellSettings = cellEl.hasAttribute("data-cell-id") || cellEl.hasAttribute("data-cell-class") || hasCellStyleClass;
        
        if (!hasCustomCellSettings) {
          // Only apply table-level border styles to cells without custom settings
          cellEl.style.borderColor = tableSettings.tableBorderColor;
          cellEl.style.borderStyle = "solid";

          // Set border widths using 4-value shorthand (top right bottom left)
          // Remove top border for non-first rows and left border for non-first columns
          // to prevent double borders where cells meet. With a row gap the rows
          // stand apart, so every row keeps its own top border.
          cellEl.style.borderWidth = cellBorderWidthValue(borderW, isFirstRow, isFirstCol, rowGap > 0);
        } else {
          // Only reset border-radius for cells without custom settings
          cellEl.style.borderRadius = "0";
          // The table border colour is a document-wide decision, so repoint the
          // cell's own border settings at it. Without this the generated
          // per-cell `!important` CSS keeps repainting the old colour and the
          // table border colour appears not to change at all.
          recolorCellBorder(cellEl, tableSettings.tableBorderColor);
        }
        
        // When table border-radius is applied, handle corner cells specially
        if (hasRadius) {
          const radius = tableSettings.tableBorderRadius;
          
          // Top-left corner
          if (isFirstRow && isFirstCol) {
            cellEl.style.borderTopLeftRadius = `${radius}px`;
          }
          // Top-right corner
          if (isFirstRow && isLastCol) {
            cellEl.style.borderTopRightRadius = `${radius}px`;
          }
          // Bottom-left corner
          if (isLastRow && isFirstCol) {
            cellEl.style.borderBottomLeftRadius = `${radius}px`;
          }
          // Bottom-right corner
          if (isLastRow && isLastCol) {
            cellEl.style.borderBottomRightRadius = `${radius}px`;
          }
        }
      });
    });

    // Generate base CSS for border styles (not responsive)
    // Always use cell borders only (no table border) to prevent double borders
    let baseTableCSS = `
table[data-table-id="${tableId}"] {
  border-collapse: separate !important;
  border-spacing: 0 ${rowGap}px !important;
  border: none !important;
  ${hasRadius ? `
  border-radius: ${tableSettings.tableBorderRadius}px !important;
  overflow: hidden !important;
  ` : `
  border-radius: 0 !important;
  `}
}`;

    // Add responsive row gap styles via media queries - only for breakpoints with explicit row gaps
    Object.entries(BREAKPOINTS).forEach(([key, bp]) => {
      const bpRowGap = tableSettings.tableWidths?.[key as BreakpointKey]?.rowGap;
      // Generate media query if rowGap is explicitly set (including 0 to override other breakpoints)
      if (bpRowGap !== undefined) {
        baseTableCSS += `
@media (min-width: ${bp.min}px) ${bp.max !== Infinity ? `and (max-width: ${bp.max}px)` : ""} {
  table[data-table-id="${tableId}"] {
    border-spacing: 0 ${bpRowGap}px !important;
  }
}`;
      }
    });

    // Add corner cell border-radius styles when radius is set
    if (hasRadius) {
      const radius = tableSettings.tableBorderRadius;
      baseTableCSS += `
table[data-table-id="${tableId}"] tr:first-child th:first-child,
table[data-table-id="${tableId}"] tr:first-child td:first-child {
  border-top-left-radius: ${radius}px !important;
}
table[data-table-id="${tableId}"] tr:first-child th:last-child,
table[data-table-id="${tableId}"] tr:first-child td:last-child {
  border-top-right-radius: ${radius}px !important;
}
table[data-table-id="${tableId}"] tr:last-child td:first-child,
table[data-table-id="${tableId}"] tr:last-child th:first-child {
  border-bottom-left-radius: ${radius}px !important;
}
table[data-table-id="${tableId}"] tr:last-child td:last-child,
table[data-table-id="${tableId}"] tr:last-child th:last-child {
  border-bottom-right-radius: ${radius}px !important;
}`;
    }
    
    // Add border collapse fix to prevent double borders
    // Only set border-style, let cells manage their own colors/widths
    baseTableCSS += `
/* Ensure cells have solid borders and prevent duplicates */
table[data-table-id="${tableId}"] td:not([data-cell-id]):not([data-cell-class]),
table[data-table-id="${tableId}"] th:not([data-cell-id]):not([data-cell-class]) {
  border-style: solid;
}
table[data-table-id="${tableId}"] td:not(:first-child):not([data-cell-id]):not([data-cell-class]),
table[data-table-id="${tableId}"] th:not(:first-child):not([data-cell-id]):not([data-cell-class]) {
  border-left-width: 0;
}
table[data-table-id="${tableId}"] tr:not(:first-child) td:not([data-cell-id]):not([data-cell-class]),
table[data-table-id="${tableId}"] tr:not(:first-child) th:not([data-cell-id]):not([data-cell-class]) {
  border-top-width: 0;
}`;

    // Generate responsive CSS
    let responsiveCSS = baseTableCSS;

    // Only generate responsive CSS if there are custom table widths
    if (tableSettings.tableWidths && Object.keys(tableSettings.tableWidths).length > 0) {
      Object.entries(BREAKPOINTS).forEach(([key, bp]) => {
        const widthConfig = tableSettings.tableWidths[key as BreakpointKey];
        if (!widthConfig) return;
        
        // Build table CSS for this breakpoint
        let tableStyles = "";
        let hasCustomSettings = false;
        
        // Width styles
        if (widthConfig.type !== undefined && widthConfig.value) {
          const widthValue = widthConfig.type === "percent" ? `${widthConfig.value}%` : `${widthConfig.value}px`;
          tableStyles += `width: ${widthValue} !important; min-width: ${widthValue} !important; max-width: ${widthValue} !important; table-layout: fixed !important; `;
          hasCustomSettings = true;
        }
        
        // Alignment styles
        if (widthConfig.alignment && widthConfig.alignment !== "left") {
          tableStyles += getAlignmentCSS(widthConfig.alignment);
          hasCustomSettings = true;
        }
        
        // Horizontal scroll needs its own wrapper rule even when no width was
        // set, so it cannot be gated behind hasCustomSettings.
        if (hasCustomSettings || widthConfig.horizontalScroll) {
          // Build wrapper CSS for scroll
          const wrapperStyles = widthConfig.horizontalScroll
            ? "overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; display: block;"
            : "";

          responsiveCSS += `
@media (min-width: ${bp.min}px) ${bp.max !== Infinity ? `and (max-width: ${bp.max}px)` : ""} {
  table[data-table-id="${tableId}"] {
    ${tableStyles}
  }${wrapperStyles ? `
  .table-wrapper[data-for-table="${tableId}"] {
    ${wrapperStyles}
  }` : ""}
}`;
        }
      });
    }

    // Clean up nested wrappers for THIS table only (don't affect other tables)
    cleanupTableWrappers(doc, tableId);
    
    // Remove all existing style tags
    doc.querySelectorAll(`style[data-table-style]`).forEach(s => s.remove());
    doc.querySelectorAll(`style[data-column-style]`).forEach(s => s.remove());
    doc.querySelectorAll(`style[data-cell-style]`).forEach(s => s.remove());
    
    // Re-find the table after cleanup
    const cleanTable = findTable(doc);
    if (!cleanTable) return;

    // Re-apply table ID after cleanup (individual attributes already set on main table)
    cleanTable.setAttribute("data-table-id", tableId);

    // Apply cleanup to the table
    cleanupTableStyles(cleanTable);

    // Create a single wrapper for the table
    const wrapper = createTableWrapper(doc, tableId);
    cleanTable.parentNode?.insertBefore(wrapper, cleanTable);
    wrapper.appendChild(cleanTable);
    
    // Ensure all OTHER tables still have their wrappers
    ensureOtherTablesHaveWrappers(doc, tableId);
    
    // Always apply inline width styles based on data-width-xs attribute AFTER cleanup
    // This ensures width is preserved even after cleanup
    const widthAttr = cleanTable.getAttribute("data-width-xs") || "percent:100:left:0:";
    const [widthType, widthValue] = widthAttr.split(":");
    if (widthType === "percent") {
      cleanTable.style.width = `${widthValue}%`;
      cleanTable.style.minWidth = `${widthValue}%`;
      cleanTable.style.maxWidth = `${widthValue}%`;
    } else if (widthType === "pixel" || widthType === "fixed") {
      cleanTable.style.width = `${widthValue}px`;
      cleanTable.style.minWidth = `${widthValue}px`;
      cleanTable.style.maxWidth = `${widthValue}px`;
    }
    cleanTable.style.tableLayout = "fixed";

    // Get the final HTML
    let finalHtml = doc.body.innerHTML;
    
    // Prepend preserved non-table style tags (responsive font/weight/align styles)
    if (preservedStyleTags.length > 0) {
      finalHtml = preservedStyleTags.join('') + finalHtml;
    }
    
    // Remove any remaining style tags from HTML string
    finalHtml = removeStyleTags(finalHtml);
    
    // Build all CSS into ONE consolidated style block using helper
    // This reads from individual data attributes (data-col-width-*, data-width-*, etc.)
    // and preserves all existing table, column, row, and cell styles
    let allCSS = regenerateAllCSS(finalHtml, tableId);
    
    // Also add the current table's responsive CSS
    if (responsiveCSS) {
      allCSS += responsiveCSS;
    }
    
    // Regenerate ALL CELL CSS from table-level data-cell-styles
    const cellStylesMatch = finalHtml.match(/data-cell-styles="([^"]+)"/);
    if (cellStylesMatch) {
      try {
        const cellStylesData = JSON.parse(cellStylesMatch[1].replace(/&quot;/g, '"'));
        for (const [cellClass, cellData] of Object.entries(cellStylesData)) {
          const typedCellData = cellData as { s: Record<string, Record<string, unknown>> };
          if (typedCellData.s) {
            allCSS += generateCellClassCSS(cellClass, typedCellData.s, tableId);
          }
        }
      } catch {
        // Ignore parse errors
      }
    }
    
    // Also handle legacy data-cell-id for backwards compatibility
    const legacyCellMatches = finalHtml.matchAll(/data-cell-id="([^"]+)"[^>]*data-cell-settings="([^"]+)"/g);
    for (const match of legacyCellMatches) {
      const existingCellId = match[1];
      
      try {
        const cellData = JSON.parse(match[2].replace(/&quot;/g, '"'));
        const cellStyles = cellData.s || cellData.styles;
        if (cellStyles) {
          allCSS += generateCellCSS(existingCellId, cellStyles, tableId);
        }
      } catch {
        // Ignore parse errors
      }
    }
    
    // Also handle legacy data-cell-class with data-cell-settings on each cell
    const legacyClassMatches = finalHtml.matchAll(/data-cell-class="([^"]+)"[^>]*data-cell-settings="([^"]+)"/g);
    const processedLegacyClasses = new Set<string>();
    for (const match of legacyClassMatches) {
      const cellClass = match[1];
      if (processedLegacyClasses.has(cellClass)) continue;
      processedLegacyClasses.add(cellClass);
      
      try {
        const cellData = JSON.parse(match[2].replace(/&quot;/g, '"'));
        const cellStyles = cellData.s || cellData.styles;
        if (cellStyles) {
          allCSS += generateCellClassCSS(cellClass, cellStyles, tableId);
        }
      } catch {
        // Ignore parse errors
      }
    }
    
    // 5. Fallback: Find cells with cell-style-* classes but no data-cell-styles on table
    // Extract inline styles and regenerate CSS
    const cellStyleClassMatches = finalHtml.matchAll(/class="[^"]*\b(cell-style-\w+)\b[^"]*"[^>]*style="([^"]+)"/g);
    const processedStyleClasses = new Set<string>();
    for (const match of cellStyleClassMatches) {
      const cellClass = match[1];
      if (processedStyleClasses.has(cellClass)) continue;
      processedStyleClasses.add(cellClass);
      
      // Check if this class already has CSS from data-cell-styles
      if (allCSS.includes(`.${cellClass}`)) continue;
      
      // Parse inline styles to generate CSS
      const inlineStyle = match[2];
      const styleRules: string[] = [];
      
      // Extract key styles from inline
      const bgMatch = inlineStyle.match(/background-color:\s*([^;]+)/i);
      if (bgMatch) styleRules.push(`background-color:${bgMatch[1].trim()}!important`);
      
      const colorMatch = inlineStyle.match(/(?<!background-)color:\s*([^;]+)/i);
      if (colorMatch) styleRules.push(`color:${colorMatch[1].trim()}!important`);
      
      const paddingMatch = inlineStyle.match(/padding:\s*([^;]+)/i);
      if (paddingMatch) styleRules.push(`padding:${paddingMatch[1].trim()}!important`);
      
      const borderColorMatch = inlineStyle.match(/border-color:\s*([^;]+)/i);
      if (borderColorMatch) styleRules.push(`border-color:${borderColorMatch[1].trim()}!important`);
      
      const borderStyleMatch = inlineStyle.match(/border-style:\s*([^;]+)/i);
      if (borderStyleMatch) styleRules.push(`border-style:${borderStyleMatch[1].trim()}!important`);
      
      const borderWidthMatch = inlineStyle.match(/border-width:\s*([^;]+)/i);
      if (borderWidthMatch) styleRules.push(`border-width:${borderWidthMatch[1].trim()}!important`);
      
      if (styleRules.length > 0) {
        allCSS += `table[data-table-id="${tableId}"] td.${cellClass},table[data-table-id="${tableId}"] th.${cellClass}{${styleRules.join(';')}}`;
      }
    }
    
    // Generate CSS for ALL tables in the document
    const allStyleTags = generateCSSForAllTables(finalHtml);
    if (allStyleTags) {
      finalHtml = finalHtml + allStyleTags;
    }
    
    // Convert data attributes to use single quotes to save space (avoids &quot; entities)
    finalHtml = convertToSingleQuoteAttributes(finalHtml);
    
    isInternalChange.current = true;
    editor.commands.setContent(finalHtml, { emitUpdate: true, parseOptions: { preserveWhitespace: 'full' } });
    onChange?.(finalHtml);
    isInternalChange.current = false;
    
    // Restore cursor position to the saved cell
    setTimeout(() => {
      if (editor && savedRowIndex !== null && savedColIndex !== null) {
        let targetCellPos: number | null = null;
        let currentRow = 0;
        
        editor.state.doc.descendants((node, pos) => {
          if (targetCellPos !== null) return false;
          
          if (node.type.name === "tableRow") {
            if (currentRow === savedRowIndex) {
              let currentCol = 0;
              node.forEach((childNode, offset) => {
                if (targetCellPos !== null) return;
                if (childNode.type.name === "tableCell" || childNode.type.name === "tableHeader") {
                  if (currentCol === savedColIndex) {
                    targetCellPos = pos + offset + 2; // +2 to position inside the cell
                  }
                  currentCol++;
                }
              });
            }
            currentRow++;
          }
        });
        
        if (targetCellPos !== null) {
          editor.commands.focus();
          editor.commands.setTextSelection(targetCellPos);
        }
      }
    }, 50);
    
    toast({
      title: "Table Settings Applied",
      description: "Table container settings have been applied successfully.",
    });
  }, [editor, tableSettings, onChange, isInternalChange, currentTableId]);

  // Apply column settings
  const applyColumnStyles = useCallback(() => {
    if (!editor) return;

    // Save current cell position
    const savedRowIndex = getCurrentRowIndex(editor);
    const savedColIndex = getCurrentColumnIndex(editor);

    const colIndex = columnSettings.columnIndex;
    
    // Save column settings to React state for persistence
    setAllColumnSettings((prev) => ({
      ...prev,
      [colIndex]: { ...columnSettings },
    }));

    // Get current HTML and modify via DOM
    const originalHtml = editor.getHTML();
    // Extract non-table style tags BEFORE parsing (DOMParser moves <style> to <head>)
    const { html, styleTags: preservedStyleTags } = extractNonTableStyleTags(originalHtml);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    let table = findTable(doc);
    
    if (!table) return;

    // Get table ID for wrapper
    const tableId = table.getAttribute("data-table-id") || `table-${Date.now()}`;

    // Clean up nested wrappers for THIS table only (don't affect other tables)
    cleanupTableWrappers(doc, tableId);
    
    // Re-find table after wrapper cleanup
    table = findTable(doc);
    if (!table) return;
    
    // Re-apply table ID after cleanup (important!)
    table.setAttribute("data-table-id", tableId);
    
    // Ensure table has default width settings if none exist
    if (!table.getAttribute("data-width-xs")) {
      table.setAttribute("data-width-xs", "percent:100:left:0:");
    }
    
    // Clean up table
    cleanupTableStyles(table);
    
    // Create proper wrapper
    const wrapper = createTableWrapper(doc, tableId);
    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);
    
    // Ensure all OTHER tables still have their wrappers
    ensureOtherTablesHaveWrappers(doc, tableId);
    
    // Always apply inline width styles based on data-width-xs attribute
    // This ensures width is preserved even after cleanup
    const widthAttr = table.getAttribute("data-width-xs") || "percent:100:left:0:";
    const [widthType, widthValue] = widthAttr.split(":");
    if (widthType === "percent") {
      table.style.width = `${widthValue}%`;
      table.style.minWidth = `${widthValue}%`;
      table.style.maxWidth = `${widthValue}%`;
    } else if (widthType === "pixel" || widthType === "fixed") {
      table.style.width = `${widthValue}px`;
      table.style.minWidth = `${widthValue}px`;
      table.style.maxWidth = `${widthValue}px`;
    }
    table.style.tableLayout = "fixed";

    // Remove conflicting styles from colgroup/col elements for current column
    const cols = table.querySelectorAll("col");
    if (cols[colIndex]) {
      const col = cols[colIndex] as HTMLElement;
      col.style.removeProperty("min-width");
      col.style.removeProperty("width");
      col.style.removeProperty("max-width");
    }

    // Generate a hash-based class name for the column style (for CSS)
    const colStyleHash = JSON.stringify(columnSettings.styles);
    const columnClassName = `col-style-${hashCode(colStyleHash)}`;

    // Mark all cells in this column with the class and individual width attributes
    const rows = table.querySelectorAll("tr");
    const breakpoints: BreakpointKey[] = ["xs", "sm", "md", "lg", "xl"];
    
    rows.forEach((row) => {
      const cells = row.querySelectorAll("td, th");
      const cell = cells[colIndex] as HTMLElement;
      if (cell) {
        cell.setAttribute("data-column-index", String(colIndex));
        
        // Remove old col-style-* classes and add the new one
        const existingClasses = cell.getAttribute("class") || "";
        const classArray = existingClasses.split(" ").filter(c => c && !c.startsWith("col-style-"));
        classArray.push(columnClassName);
        cell.setAttribute("class", classArray.join(" "));
        
        // Store column width settings as individual attributes on each cell (like cell styles)
        // Format: "type:value" (e.g., "percent:25" or "pixel:200" or "auto:")
        breakpoints.forEach((bp) => {
          const config = columnSettings.styles[bp];
          if (config) {
            const widthStr = `${config.widthType || "auto"}:${config.widthValue || ""}`;
            cell.setAttribute(`data-col-width-${bp}`, widthStr);
          }
        });
        
        // Remove conflicting inline width styles
        cell.style.removeProperty("min-width");
        cell.style.removeProperty("width");
        cell.style.removeProperty("max-width");
      }
    });

    // Get the final HTML
    let finalHtml = doc.body.innerHTML;
    
    // Prepend preserved non-table style tags (responsive font/weight/align styles)
    if (preservedStyleTags.length > 0) {
      finalHtml = preservedStyleTags.join('') + finalHtml;
    }
    
    // Remove ALL existing style tags
    finalHtml = removeStyleTags(finalHtml);
    
    // Build all CSS into ONE consolidated style block using helper
    let allCSS = regenerateAllCSS(finalHtml, tableId);
    
    // 4. Regenerate ALL CELL CSS from table-level data-cell-styles
    const cellStylesMatch = finalHtml.match(/data-cell-styles="([^"]+)"/);
    if (cellStylesMatch) {
      try {
        const cellStylesData = JSON.parse(cellStylesMatch[1].replace(/&quot;/g, '"'));
        for (const [cellClass, cellData] of Object.entries(cellStylesData)) {
          const typedCellData = cellData as { s: Record<string, Record<string, unknown>> };
          if (typedCellData.s) {
            allCSS += generateCellClassCSS(cellClass, typedCellData.s, tableId);
          }
        }
      } catch {
        // Ignore parse errors
      }
    }
    
    // Also handle legacy data-cell-id for backwards compatibility
    const legacyCellMatches = finalHtml.matchAll(/data-cell-id="([^"]+)"[^>]*data-cell-settings="([^"]+)"/g);
    for (const match of legacyCellMatches) {
      const existingCellId = match[1];
      
      try {
        const cellData = JSON.parse(match[2].replace(/&quot;/g, '"'));
        const cellStyles = cellData.s || cellData.styles;
        if (cellStyles) {
          allCSS += generateCellCSS(existingCellId, cellStyles, tableId);
        }
      } catch {
        // Ignore parse errors
      }
    }
    
    // Also handle legacy data-cell-class with data-cell-settings on each cell
    const legacyClassMatches = finalHtml.matchAll(/data-cell-class="([^"]+)"[^>]*data-cell-settings="([^"]+)"/g);
    const processedLegacyClasses = new Set<string>();
    for (const match of legacyClassMatches) {
      const cellClass = match[1];
      if (processedLegacyClasses.has(cellClass)) continue;
      processedLegacyClasses.add(cellClass);
      
      try {
        const cellData = JSON.parse(match[2].replace(/&quot;/g, '"'));
        const cellStyles = cellData.s || cellData.styles;
        if (cellStyles) {
          allCSS += generateCellClassCSS(cellClass, cellStyles, tableId);
        }
      } catch {
        // Ignore parse errors
      }
    }
    
    // Fallback: Find cells with cell-style-* classes but no data-cell-styles on table
    const cellStyleClassMatches = finalHtml.matchAll(/class="[^"]*\b(cell-style-\w+)\b[^"]*"[^>]*style="([^"]+)"/g);
    const processedStyleClasses = new Set<string>();
    for (const match of cellStyleClassMatches) {
      const cellClass = match[1];
      if (processedStyleClasses.has(cellClass)) continue;
      processedStyleClasses.add(cellClass);
      if (allCSS.includes(`.${cellClass}`)) continue;
      
      const inlineStyle = match[2];
      const styleRules: string[] = [];
      const bgMatch = inlineStyle.match(/background-color:\s*([^;]+)/i);
      if (bgMatch) styleRules.push(`background-color:${bgMatch[1].trim()}!important`);
      const colorMatch = inlineStyle.match(/(?<!background-)color:\s*([^;]+)/i);
      if (colorMatch) styleRules.push(`color:${colorMatch[1].trim()}!important`);
      const paddingMatch = inlineStyle.match(/padding:\s*([^;]+)/i);
      if (paddingMatch) styleRules.push(`padding:${paddingMatch[1].trim()}!important`);
      const borderColorMatch = inlineStyle.match(/border-color:\s*([^;]+)/i);
      if (borderColorMatch) styleRules.push(`border-color:${borderColorMatch[1].trim()}!important`);
      const borderStyleMatch = inlineStyle.match(/border-style:\s*([^;]+)/i);
      if (borderStyleMatch) styleRules.push(`border-style:${borderStyleMatch[1].trim()}!important`);
      const borderWidthMatch = inlineStyle.match(/border-width:\s*([^;]+)/i);
      if (borderWidthMatch) styleRules.push(`border-width:${borderWidthMatch[1].trim()}!important`);
      
      if (styleRules.length > 0) {
        allCSS += `table[data-table-id="${tableId}"] td.${cellClass},table[data-table-id="${tableId}"] th.${cellClass}{${styleRules.join(';')}}`;
      }
    }
    
    // Generate CSS for ALL tables in the document
    const allStyleTags = generateCSSForAllTables(finalHtml);
    if (allStyleTags) {
      finalHtml = finalHtml + allStyleTags;
    }
    
    // Convert data attributes to use single quotes to save space (avoids &quot; entities)
    finalHtml = convertToSingleQuoteAttributes(finalHtml);
    
    isInternalChange.current = true;
    editor.commands.setContent(finalHtml, { emitUpdate: true, parseOptions: { preserveWhitespace: 'full' } });
    onChange?.(finalHtml);
    isInternalChange.current = false;
    
    // Restore cursor position to the saved cell
    setTimeout(() => {
      if (editor && savedRowIndex !== null && savedColIndex !== null) {
        let targetCellPos: number | null = null;
        let currentRow = 0;
        
        editor.state.doc.descendants((node, pos) => {
          if (targetCellPos !== null) return false;
          
          if (node.type.name === "tableRow") {
            if (currentRow === savedRowIndex) {
              let currentCol = 0;
              node.forEach((childNode, offset) => {
                if (targetCellPos !== null) return;
                if (childNode.type.name === "tableCell" || childNode.type.name === "tableHeader") {
                  if (currentCol === savedColIndex) {
                    targetCellPos = pos + offset + 2; // +2 to position inside the cell
                  }
                  currentCol++;
                }
              });
            }
            currentRow++;
          }
        });
        
        if (targetCellPos !== null) {
          editor.commands.focus();
          editor.commands.setTextSelection(targetCellPos);
        }
      }
    }, 50);
    
    // Validation warnings
    validateColumnWidths(finalHtml, colIndex, allColumnSettings, columnSettings);
    
    toast({
      title: "Column Settings Applied",
      description: `Column ${colIndex + 1} settings have been applied successfully.`,
    });
  }, [editor, columnSettings, allColumnSettings, onChange, isInternalChange, setAllColumnSettings, currentTableId]);

  // Apply cell settings
  const applyCellStyles = useCallback(() => {
    if (!editor) return;

    // Save current cell position
    const savedRowIndex = getCurrentRowIndex(editor);
    const savedColIndex = getCurrentColumnIndex(editor);

    // Get global colors from xs breakpoint (colors are the same for all breakpoints)
    const xsColorConfig = cellSettings.styles?.xs;
    const globalBgColor = xsColorConfig?.backgroundColor || "";
    const globalTextColor = xsColorConfig?.textColor || "";

    // Helper to create padding string
    const getPaddingStr = (p: { top?: string; right?: string; bottom?: string; left?: string } | undefined) => {
      if (!p) return null;
      if (p.top === "8" && p.right === "12" && p.bottom === "8" && p.left === "12") return null; // default
      return `${p.top},${p.right},${p.bottom},${p.left}`;
    };

    // Helper to create border string
    const getBorderStr = (b: { top?: { color?: string; width?: string }; right?: { color?: string; width?: string }; bottom?: { color?: string; width?: string }; left?: { color?: string; width?: string } } | undefined) => {
      if (!b) return null;
      const parts: string[] = [];
      const defaultBorderColor = "#e5e7eb";
      const defaultBorderWidth = "1";
      if ((b.top?.color && b.top.color !== defaultBorderColor) || (b.top?.width && b.top.width !== defaultBorderWidth)) {
        parts.push(`t:${b.top?.color||''}:${b.top?.width||''}`);
      }
      if ((b.right?.color && b.right.color !== defaultBorderColor) || (b.right?.width && b.right.width !== defaultBorderWidth)) {
        parts.push(`r:${b.right?.color||''}:${b.right?.width||''}`);
      }
      if ((b.bottom?.color && b.bottom.color !== defaultBorderColor) || (b.bottom?.width && b.bottom.width !== defaultBorderWidth)) {
        parts.push(`b:${b.bottom?.color||''}:${b.bottom?.width||''}`);
      }
      if ((b.left?.color && b.left.color !== defaultBorderColor) || (b.left?.width && b.left.width !== defaultBorderWidth)) {
        parts.push(`l:${b.left?.color||''}:${b.left?.width||''}`);
      }
      return parts.length > 0 ? parts.join(';') : null;
    };

    // First, build xs config (the base)
    const xsBaseConfig = cellSettings.styles?.xs;
    const xsPadding = getPaddingStr(xsBaseConfig?.padding);
    const xsBorder = getBorderStr(xsBaseConfig?.border);

    // Create minimal cell settings - only store non-empty values to reduce size
    // Colors are only stored in xs (since they're global)
    // Other breakpoints only store values that DIFFER from xs
    const minimalStyles: Record<string, Record<string, unknown>> = {};
    Object.entries(cellSettings.styles || {}).forEach(([bp, config]) => {
      const minimal: Record<string, unknown> = {};
      
      // Only save colors in xs breakpoint (since colors are global)
      if (bp === "xs") {
        if (globalBgColor) minimal.bg = globalBgColor;
        if (globalTextColor) minimal.c = globalTextColor;
        // xs always stores its values (as the base)
        if (config.textAlign && config.textAlign !== "left") minimal.ta = config.textAlign;
        if (config.verticalAlign && config.verticalAlign !== "middle") minimal.va = config.verticalAlign;
        if (config.fontSize) minimal.fs = config.fontSize;
        if (config.fontWeight) minimal.fw = config.fontWeight;
        if (xsPadding) minimal.p = xsPadding;
        if (xsBorder) minimal.bd = xsBorder;
      } else {
        // For other breakpoints, only store values that are DIFFERENT from xs
        if (config.textAlign && config.textAlign !== "left" && config.textAlign !== xsBaseConfig?.textAlign) {
          minimal.ta = config.textAlign;
        }
        if (config.verticalAlign && config.verticalAlign !== "middle" && config.verticalAlign !== xsBaseConfig?.verticalAlign) {
          minimal.va = config.verticalAlign;
        }
        if (config.fontSize && config.fontSize !== xsBaseConfig?.fontSize) {
          minimal.fs = config.fontSize;
        }
        if (config.fontWeight && config.fontWeight !== xsBaseConfig?.fontWeight) {
          minimal.fw = config.fontWeight;
        }
        // Only store padding if different from xs
        const bpPadding = getPaddingStr(config.padding);
        if (bpPadding && bpPadding !== xsPadding) {
          minimal.p = bpPadding;
        }
        // Only store border if different from xs
        const bpBorder = getBorderStr(config.border);
        if (bpBorder && bpBorder !== xsBorder) {
          minimal.bd = bpBorder;
        }
      }
      // Only store breakpoint if it has any values
      if (Object.keys(minimal).length > 0) {
        minimalStyles[bp] = minimal;
      }
    });
    
    // Generate a hash from the minimal styles to use as a shared class name
    const styleHash = JSON.stringify(minimalStyles);
    const cellClassName = `cell-style-${hashCode(styleHash)}`;
    const cellSettingsJson = JSON.stringify({ s: minimalStyles });

    // Get HTML and modify via DOM
    const originalHtml = editor.getHTML();
    // Extract non-table style tags BEFORE parsing (DOMParser moves <style> to <head>)
    const { html, styleTags: preservedStyleTags } = extractNonTableStyleTags(originalHtml);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    let table = findTable(doc);

    if (!table) return;

    // Get table ID for wrapper
    const tableId = table.getAttribute("data-table-id") || `table-${Date.now()}`;

    // Clean up nested wrappers for THIS table only (don't affect other tables)
    cleanupTableWrappers(doc, tableId);
    
    // Re-find table after wrapper cleanup
    table = findTable(doc);
    if (!table) return;
    
    // Re-apply table ID after cleanup (important!)
    table.setAttribute("data-table-id", tableId);
    
    // Ensure table has default width settings if none exist
    if (!table.getAttribute("data-width-xs")) {
      table.setAttribute("data-width-xs", "percent:100:left:0:");
    }
    
    // Clean up table
    cleanupTableStyles(table);
    
    // Create proper wrapper
    const wrapper = createTableWrapper(doc, tableId);
    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);
    
    // Ensure all OTHER tables still have their wrappers
    ensureOtherTablesHaveWrappers(doc, tableId);
    
    // Always apply inline width styles based on data-width-xs attribute
    // This ensures width is preserved even after cleanup
    const widthAttr = table.getAttribute("data-width-xs") || "percent:100:left:0:";
    const [widthType, widthValue] = widthAttr.split(":");
    if (widthType === "percent") {
      table.style.width = `${widthValue}%`;
      table.style.minWidth = `${widthValue}%`;
      table.style.maxWidth = `${widthValue}%`;
    } else if (widthType === "pixel" || widthType === "fixed") {
      table.style.width = `${widthValue}px`;
      table.style.minWidth = `${widthValue}px`;
      table.style.maxWidth = `${widthValue}px`;
    }
    table.style.tableLayout = "fixed";

    // Get all cells from THE CURRENT TABLE ONLY (not all tables in the document)
    const docCells = Array.from(table.querySelectorAll("td, th"));
    
    // PRIORITY: Try to detect CURRENT selection first (more accurate when user clicks different cells)
    // Only fall back to captured indices if current detection fails
    let selectedIndices: number[] = [];
    
    console.log("applyCellStyles - currentTableId:", currentTableId);
    
    // Use editor.view.dom to query directly within the TipTap editor DOM
    const editorDom = editor.view.dom;
    
    // Debug: Check what tables exist in the editor DOM
    const allTables = editorDom.querySelectorAll("table");
    console.log("applyCellStyles - All tables in editor:", allTables.length);
    allTables.forEach((t, i) => {
      console.log(`  Table ${i}: data-table-id="${t.getAttribute("data-table-id")}"`);
    });
    
    const pmTable = currentTableId 
      ? editorDom.querySelector(`table[data-table-id="${currentTableId}"]`)
      : editorDom.querySelector("table");
    
    console.log("applyCellStyles - pmTable found:", !!pmTable);
    
    // PRIMARY METHOD: Use captured indices from when modal opened (most reliable for multi-select)
    if (selectedCellIndices.length > 0) {
      selectedIndices = selectedCellIndices;
      console.log("Method 1 (PRIMARY) - Using captured selectedCellIndices:", selectedIndices);
    }
    // FALLBACK METHOD 1: Check for .selectedCell class in current DOM
    else if (pmTable) {
      const pmCells = Array.from(pmTable.querySelectorAll("td, th"));
      
      pmCells.forEach((cell, idx) => {
        if (cell.classList.contains("selectedCell") || cell.classList.contains("ProseMirror-selectednode")) {
          selectedIndices.push(idx);
        }
      });
      console.log("Method 2 (Fallback) - .selectedCell class:", selectedIndices.length, "cells found");
    }
    
    // FALLBACK METHOD 2: Use editor state to find single cursor cell
    if (selectedIndices.length === 0 && pmTable) {
      const { $from } = editor.state.selection;
      const selectionPos = $from.pos;
      console.log("Method 3 (Fallback) - Selection pos:", selectionPos, "depth:", $from.depth);
      
      // Iterate through all cells in the CURRENT table and find which one contains the selection
      let cellIndex = 0;
      let foundTableId: string | null = null;
      
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === "table") {
          foundTableId = node.attrs["data-table-id"] || null;
          console.log("Method 3 - Table found:", foundTableId, "match:", foundTableId === currentTableId);
          cellIndex = 0; // Reset cell counter for each table
          // Only traverse this table if it's the current table or if we don't have a currentTableId
          return foundTableId === currentTableId || !currentTableId;
        }
        
        if ((node.type.name === "tableCell" || node.type.name === "tableHeader") && 
            (foundTableId === currentTableId || !currentTableId)) {
          const cellStart = pos;
          const cellEnd = pos + node.nodeSize;
          
          // Check if selection position is within this cell
          if (selectionPos >= cellStart && selectionPos < cellEnd) {
            console.log("Method 3 - Found cell at index:", cellIndex, "pos range:", cellStart, "-", cellEnd);
            if (!selectedIndices.includes(cellIndex)) {
              selectedIndices.push(cellIndex);
            }
          }
          cellIndex++;
        }
        return true;
      });
      
      console.log("Method 3 result:", selectedIndices);
    }
    
    console.log("applyCellStyles - FINAL docCells:", docCells.length, "selectedIndices:", selectedIndices);

    // NO JSON storage - cell styles are stored as inline styles on cells + CSS class
    // The CSS is generated from inline styles when regenerating
    // Remove any legacy data-cell-styles attribute
    table.removeAttribute("data-cell-styles");

    // Mark selected cells with data attributes and apply inline styles
    // Use xs breakpoint styles for inline styles (immediate visual effect)
    const xsConfig = cellSettings.styles?.xs;
    
    selectedIndices.forEach((idx) => {
      const cell = docCells[idx] as HTMLElement;
      if (cell) {
        // Use shared class name - CSS is generated from inline styles
        cell.setAttribute("data-cell-class", cellClassName);
        // Store compact settings so we can read them back accurately
        cell.setAttribute("data-cell-settings", cellSettingsJson);
        
        // Add the class to the cell
        const existingClasses = cell.getAttribute("class") || "";
        const classArray = existingClasses.split(" ").filter(c => c && !c.startsWith("cell-style-"));
        classArray.push(cellClassName);
        cell.setAttribute("class", classArray.join(" "));
        
        // Also apply inline styles for immediate effect and compatibility
        if (xsConfig) {
          if (xsConfig.backgroundColor) {
            cell.style.backgroundColor = xsConfig.backgroundColor;
          }
          if (xsConfig.textColor) {
            cell.style.color = xsConfig.textColor;
          }
          
          // Apply 4-sided border styles
          // Only set borders that have a non-default color
          if (xsConfig.border) {
            const b = xsConfig.border;
            const defaultBorderColor = "#e5e7eb";
            
            // Top
            if (b.top?.color && b.top.color !== defaultBorderColor) {
              cell.style.borderTopColor = b.top.color;
              cell.style.borderTopStyle = 'solid';
              cell.style.borderTopWidth = b.top.width ? `${b.top.width}px` : '1px';
            }
            
            // Right
            if (b.right?.color && b.right.color !== defaultBorderColor) {
              cell.style.borderRightColor = b.right.color;
              cell.style.borderRightStyle = 'solid';
              cell.style.borderRightWidth = b.right.width ? `${b.right.width}px` : '1px';
            }
            
            // Bottom
            if (b.bottom?.color && b.bottom.color !== defaultBorderColor) {
              cell.style.borderBottomColor = b.bottom.color;
              cell.style.borderBottomStyle = 'solid';
              cell.style.borderBottomWidth = b.bottom.width ? `${b.bottom.width}px` : '1px';
            }
            
            // Left
            if (b.left?.color && b.left.color !== defaultBorderColor) {
              cell.style.borderLeftColor = b.left.color;
              cell.style.borderLeftStyle = 'solid';
              cell.style.borderLeftWidth = b.left.width ? `${b.left.width}px` : '1px';
            }
          } else if (xsConfig.borderColor) {
            // Legacy single border support
            cell.style.borderTopColor = xsConfig.borderColor;
            cell.style.borderRightColor = xsConfig.borderColor;
            cell.style.borderBottomColor = xsConfig.borderColor;
            cell.style.borderLeftColor = xsConfig.borderColor;
            cell.style.borderStyle = 'solid';
          }
          
          if (xsConfig.textAlign) {
            cell.style.textAlign = xsConfig.textAlign;
          }
          if (xsConfig.verticalAlign) {
            cell.style.verticalAlign = xsConfig.verticalAlign;
          }
          if (xsConfig.fontSize) {
            cell.style.fontSize = `${xsConfig.fontSize}px`;
          }
          if (xsConfig.fontWeight) {
            cell.style.fontWeight = xsConfig.fontWeight;
          }
          if (xsConfig.padding) {
            const p = xsConfig.padding;
            cell.style.padding = `${p.top || 8}px ${p.right || 12}px ${p.bottom || 8}px ${p.left || 12}px`;
          }
        }
      }
    });

    if (selectedIndices.length === 0) {
      toast({
        title: "No Cell Selected",
        description: "Please select a cell first.",
        variant: "destructive",
      });
      return;
    }

    // Collect cell styles metadata from cells in the CURRENT table only
    const cellStylesMetadata: Record<string, { s: Record<string, Record<string, unknown>> }> = {};
    table.querySelectorAll("td[data-cell-settings], th[data-cell-settings]").forEach((cell) => {
      // Try data-cell-class first, then extract from class attribute as fallback
      let cellClass = cell.getAttribute("data-cell-class");
      if (!cellClass) {
        const classAttr = cell.getAttribute("class") || "";
        const match = classAttr.match(/\b(cell-style-\w+)\b/);
        cellClass = match ? match[1] : null;
      }
      
      const cellSettings = cell.getAttribute("data-cell-settings");
      if (cellClass && cellSettings && !cellStylesMetadata[cellClass]) {
        try {
          cellStylesMetadata[cellClass] = JSON.parse(cellSettings);
        } catch {
          // Ignore parse errors
        }
      }
    });
    
    // Store metadata on the current table if we have any
    if (Object.keys(cellStylesMetadata).length > 0) {
      table.setAttribute("data-cell-styles", JSON.stringify(cellStylesMetadata));
    }
    
    // Get the final HTML
    let finalHtml = doc.body.innerHTML;
    
    // Prepend preserved non-table style tags (responsive font/weight/align styles)
    if (preservedStyleTags.length > 0) {
      finalHtml = preservedStyleTags.join('') + finalHtml;
    }
    
    // Remove ALL existing style tags
    finalHtml = removeStyleTags(finalHtml);
    
    // Generate CSS for ALL tables in the document (not just the current one)
    const allStyleTags = generateCSSForAllTables(finalHtml);
    if (allStyleTags) {
      finalHtml = finalHtml + allStyleTags;
    }
    
    // Convert data attributes to use single quotes to save space (avoids &quot; entities)
    finalHtml = convertToSingleQuoteAttributes(finalHtml);
    
    isInternalChange.current = true;
    editor.commands.setContent(finalHtml, { emitUpdate: true, parseOptions: { preserveWhitespace: 'full' } });
    onChange?.(finalHtml);
    isInternalChange.current = false;
    
    // Restore cursor position to the saved cell
    setTimeout(() => {
      if (editor && savedRowIndex !== null && savedColIndex !== null) {
        let targetCellPos: number | null = null;
        let currentRow = 0;
        
        editor.state.doc.descendants((node, pos) => {
          if (targetCellPos !== null) return false;
          
          if (node.type.name === "tableRow") {
            if (currentRow === savedRowIndex) {
              let currentCol = 0;
              node.forEach((childNode, offset) => {
                if (targetCellPos !== null) return;
                if (childNode.type.name === "tableCell" || childNode.type.name === "tableHeader") {
                  if (currentCol === savedColIndex) {
                    targetCellPos = pos + offset + 2; // +2 to position inside the cell
                  }
                  currentCol++;
                }
              });
            }
            currentRow++;
          }
        });
        
        if (targetCellPos !== null) {
          editor.commands.focus();
          editor.commands.setTextSelection(targetCellPos);
        }
      }
    }, 50);
    
    toast({
      title: "Cell Settings Applied",
      description: "Cell styles have been applied to selected cells.",
    });
  }, [editor, cellSettings, onChange, isInternalChange, currentTableId]);

  // Apply row settings
  const applyRowStyles = useCallback(() => {
    if (!editor) return;

    // Save current cell position
    const savedRowIndex = getCurrentRowIndex(editor);
    const savedColIndex = getCurrentColumnIndex(editor);

    const rowIndex = rowSettings.rowIndex;
    
    // Strip default values from row settings before saving
    const minimalRowStyles: Record<string, any> = {};
    Object.entries(rowSettings.styles).forEach(([bp, config]) => {
      const stripped = stripDefaultRowConfig(config);
      if (stripped) {
        minimalRowStyles[bp] = stripped;
      }
    });
    
    const minimalRowSettings = {
      rowIndex,
      styles: minimalRowStyles,
    };
    
    // Save row settings to React state for persistence
    const updatedAllRowSettings: Record<number, RowSettings> = {
      ...allRowSettings,
      [rowIndex]: minimalRowSettings as RowSettings,
    };
    setAllRowSettings(updatedAllRowSettings);

    // Get current HTML and modify via DOM
    const originalHtml = editor.getHTML();
    // Extract non-table style tags BEFORE parsing (DOMParser moves <style> to <head>)
    const { html, styleTags: preservedStyleTags } = extractNonTableStyleTags(originalHtml);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    let table = findTable(doc);
    
    if (!table) return;

    // Get table ID for wrapper
    const tableId = table.getAttribute("data-table-id") || `table-${Date.now()}`;
    table.setAttribute("data-table-id", tableId);

    // Clean up nested wrappers
    cleanupTableWrappers(doc);
    
    // Re-find table after wrapper cleanup
    table = findTable(doc);
    if (!table) return;
    
    // Re-apply table ID after cleanup (important!)
    table.setAttribute("data-table-id", tableId);
    
    // Ensure table has default width settings if none exist
    if (!table.getAttribute("data-width-xs")) {
      table.setAttribute("data-width-xs", "percent:100:left:0:");
    }
    
    // Clean up table
    cleanupTableStyles(table);
    
    // Create proper wrapper
    const wrapper = createTableWrapper(doc, tableId);
    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);
    
    // Ensure all OTHER tables still have their wrappers
    ensureOtherTablesHaveWrappers(doc, tableId);
    
    // Always apply inline width styles based on data-width-xs attribute
    // This ensures width is preserved even after cleanup
    const widthAttr = table.getAttribute("data-width-xs") || "percent:100:left:0:";
    const [widthType, widthValue] = widthAttr.split(":");
    if (widthType === "percent") {
      table.style.width = `${widthValue}%`;
      table.style.minWidth = `${widthValue}%`;
      table.style.maxWidth = `${widthValue}%`;
    } else if (widthType === "pixel" || widthType === "fixed") {
      table.style.width = `${widthValue}px`;
      table.style.minWidth = `${widthValue}px`;
      table.style.maxWidth = `${widthValue}px`;
    }
    table.style.tableLayout = "fixed";

    // Store ALL row settings on the table element as JSON (for persistence)
    table.setAttribute("data-all-row-settings", JSON.stringify(updatedAllRowSettings));

    // Apply inline styles directly to the row for immediate visual effect in editor
    // (CSS style tags don't work reliably inside contenteditable areas)
    const rows = table.querySelectorAll("tr");
    
    // Apply styles from ALL saved row settings to their respective rows
    Object.entries(updatedAllRowSettings).forEach(([rowIdxStr, savedRowData]) => {
      const savedRowSettings = savedRowData as { rowIndex: number; styles: ResponsiveRowStyles };
      const savedRowIdx = parseInt(rowIdxStr, 10);
      const targetRow = rows[savedRowIdx];
      
      if (targetRow) {
        // Use xs breakpoint styles for inline styles (editor preview)
        const xsStyles = savedRowSettings.styles?.xs;
        if (xsStyles) {
          // Apply to row
          if (xsStyles.backgroundColor) {
            (targetRow as HTMLElement).style.backgroundColor = xsStyles.backgroundColor;
          }
          if (xsStyles.textColor) {
            (targetRow as HTMLElement).style.color = xsStyles.textColor;
          }
          if (xsStyles.heightType === "pixel" && xsStyles.heightValue) {
            (targetRow as HTMLElement).style.height = `${xsStyles.heightValue}px`;
          }
          
          // Apply to all cells in the row (td and th)
          targetRow.querySelectorAll("td, th").forEach((cell) => {
            const cellEl = cell as HTMLElement;
            if (xsStyles.backgroundColor) {
              cellEl.style.backgroundColor = xsStyles.backgroundColor;
            }
            if (xsStyles.textColor) {
              cellEl.style.color = xsStyles.textColor;
            }
          });
        }
      }
    });

    // Get the final HTML
    let finalHtml = doc.body.innerHTML;
    
    // Prepend preserved non-table style tags (responsive font/weight/align styles)
    if (preservedStyleTags.length > 0) {
      finalHtml = preservedStyleTags.join('') + finalHtml;
    }
    
    // Remove ALL existing style tags
    finalHtml = removeStyleTags(finalHtml);
    
    // Build all CSS into ONE consolidated style block using helper
    // This reads from individual data attributes and preserves all existing styles
    let allCSS = regenerateAllCSS(finalHtml, tableId);
    
    // Also regenerate ROW CSS from updatedAllRowSettings (current row settings)
    for (const [rowIdxStr, savedRowData] of Object.entries(updatedAllRowSettings)) {
      const savedRowSettings = savedRowData as { rowIndex: number; styles: any };
      const savedRowIdx = parseInt(rowIdxStr, 10);
      allCSS += generateRowCSS(savedRowIdx, savedRowSettings.styles, tableId);
    }
    
    // Regenerate ALL CELL CSS from table-level data-cell-styles
    const cellStylesMatch = finalHtml.match(/data-cell-styles="([^"]+)"/);
    if (cellStylesMatch) {
      try {
        const cellStylesData = JSON.parse(cellStylesMatch[1].replace(/&quot;/g, '"'));
        for (const [cellClass, cellData] of Object.entries(cellStylesData)) {
          const typedCellData = cellData as { s: Record<string, Record<string, unknown>> };
          if (typedCellData.s) {
            allCSS += generateCellClassCSS(cellClass, typedCellData.s, tableId);
          }
        }
      } catch {
        // Ignore parse errors
      }
    }
    
    // Also handle legacy data-cell-id for backwards compatibility
    const legacyCellMatches = finalHtml.matchAll(/data-cell-id="([^"]+)"[^>]*data-cell-settings="([^"]+)"/g);
    for (const match of legacyCellMatches) {
      const existingCellId = match[1];
      
      try {
        const cellData = JSON.parse(match[2].replace(/&quot;/g, '"'));
        const cellStyles = cellData.s || cellData.styles;
        if (cellStyles) {
          allCSS += generateCellCSS(existingCellId, cellStyles, tableId);
        }
      } catch {
        // Ignore parse errors
      }
    }
    
    // Also handle legacy data-cell-class with data-cell-settings on each cell
    const legacyClassMatches = finalHtml.matchAll(/data-cell-class="([^"]+)"[^>]*data-cell-settings="([^"]+)"/g);
    const processedLegacyClasses = new Set<string>();
    for (const match of legacyClassMatches) {
      const cellClass = match[1];
      if (processedLegacyClasses.has(cellClass)) continue;
      processedLegacyClasses.add(cellClass);
      
      try {
        const cellData = JSON.parse(match[2].replace(/&quot;/g, '"'));
        const cellStyles = cellData.s || cellData.styles;
        if (cellStyles) {
          allCSS += generateCellClassCSS(cellClass, cellStyles, tableId);
        }
      } catch {
        // Ignore parse errors
      }
    }
    
    // Fallback: Find cells with cell-style-* classes but no data-cell-styles on table
    const cellStyleClassMatches = finalHtml.matchAll(/class="[^"]*\b(cell-style-\w+)\b[^"]*"[^>]*style="([^"]+)"/g);
    const processedStyleClasses = new Set<string>();
    for (const match of cellStyleClassMatches) {
      const cellClass = match[1];
      if (processedStyleClasses.has(cellClass)) continue;
      processedStyleClasses.add(cellClass);
      if (allCSS.includes(`.${cellClass}`)) continue;
      
      const inlineStyle = match[2];
      const styleRules: string[] = [];
      const bgMatch = inlineStyle.match(/background-color:\s*([^;]+)/i);
      if (bgMatch) styleRules.push(`background-color:${bgMatch[1].trim()}!important`);
      const colorMatch = inlineStyle.match(/(?<!background-)color:\s*([^;]+)/i);
      if (colorMatch) styleRules.push(`color:${colorMatch[1].trim()}!important`);
      const paddingMatch = inlineStyle.match(/padding:\s*([^;]+)/i);
      if (paddingMatch) styleRules.push(`padding:${paddingMatch[1].trim()}!important`);
      const borderColorMatch = inlineStyle.match(/border-color:\s*([^;]+)/i);
      if (borderColorMatch) styleRules.push(`border-color:${borderColorMatch[1].trim()}!important`);
      const borderStyleMatch = inlineStyle.match(/border-style:\s*([^;]+)/i);
      if (borderStyleMatch) styleRules.push(`border-style:${borderStyleMatch[1].trim()}!important`);
      const borderWidthMatch = inlineStyle.match(/border-width:\s*([^;]+)/i);
      if (borderWidthMatch) styleRules.push(`border-width:${borderWidthMatch[1].trim()}!important`);
      
      if (styleRules.length > 0) {
        allCSS += `table[data-table-id="${tableId}"] td.${cellClass},table[data-table-id="${tableId}"] th.${cellClass}{${styleRules.join(';')}}`;
      }
    }
    
    // Generate CSS for ALL tables in the document
    const allStyleTags = generateCSSForAllTables(finalHtml);
    if (allStyleTags) {
      finalHtml = finalHtml + allStyleTags;
    }
    
    // Convert data attributes to use single quotes to save space (avoids &quot; entities)
    finalHtml = convertToSingleQuoteAttributes(finalHtml);
    
    isInternalChange.current = true;
    editor.commands.setContent(finalHtml, { emitUpdate: true, parseOptions: { preserveWhitespace: 'full' } });
    onChange?.(finalHtml);
    isInternalChange.current = false;
    
    // Restore cursor position to the saved cell
    setTimeout(() => {
      if (editor && savedRowIndex !== null && savedColIndex !== null) {
        let targetCellPos: number | null = null;
        let currentRow = 0;
        
        editor.state.doc.descendants((node, pos) => {
          if (targetCellPos !== null) return false;
          
          if (node.type.name === "tableRow") {
            if (currentRow === savedRowIndex) {
              let currentCol = 0;
              node.forEach((childNode, offset) => {
                if (targetCellPos !== null) return;
                if (childNode.type.name === "tableCell" || childNode.type.name === "tableHeader") {
                  if (currentCol === savedColIndex) {
                    targetCellPos = pos + offset + 2; // +2 to position inside the cell
                  }
                  currentCol++;
                }
              });
            }
            currentRow++;
          }
        });
        
        if (targetCellPos !== null) {
          editor.commands.focus();
          editor.commands.setTextSelection(targetCellPos);
        }
      }
    }, 50);
    
    toast({
      title: "Row Settings Applied",
      description: `${rowIndex === 0 ? "Header row" : `Row ${rowIndex}`} settings have been applied successfully.`,
    });
  }, [editor, rowSettings, allRowSettings, onChange, isInternalChange, setAllRowSettings, currentTableId]);

  // Reset column widths
  const resetColumnWidths = useCallback(() => {
    if (!editor) return;

    const html = editor.getHTML();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const table = findTable(doc);
    
    if (!table) return;

    // Remove width styles from all cells
    table.querySelectorAll("td, th").forEach((cell) => {
      (cell as HTMLElement).style.width = "";
      cell.removeAttribute("data-column-widths");
    });

    // Remove column style tags
    doc.querySelectorAll("style[data-column-style]").forEach((style) => style.remove());

    const newHtml = doc.body.innerHTML;
    isInternalChange.current = true;
    editor.commands.setContent(newHtml, { emitUpdate: true, parseOptions: { preserveWhitespace: 'full' } });
    editor.commands.focus();
    onChange?.(newHtml);
    isInternalChange.current = false;
  }, [editor, onChange, isInternalChange, currentTableId]);

  // Insert table
  const insertTable = useCallback((
    tableRows: number,
    tableCols: number,
    tableWithHeader: boolean,
    tableBorderColor: string,
    tableBorderWidth: number,
  ) => {
    if (!editor) return;

    editor
      .chain()
      .focus()
      .insertTable({
        rows: tableRows,
        cols: tableCols,
        withHeaderRow: tableWithHeader,
      })
      .run();

    // Apply initial border styles
    setTimeout(() => {
      const html = editor.getHTML();
      
      // Extract non-table style tags BEFORE parsing (DOMParser moves <style> to <head>)
      const { html: htmlWithoutStyles, styleTags: preservedStyleTags } = extractNonTableStyleTags(html);
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlWithoutStyles, "text/html");
      
      // Find ALL tables and style any that don't have a data-table-id yet (newly inserted)
      const allTables = doc.querySelectorAll("table");
      
      allTables.forEach((table) => {
        // Skip tables that already have a data-table-id (already styled)
        if (table.getAttribute("data-table-id")) return;
        
        // Generate unique ID for this new table
        const tableId = `table-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        table.setAttribute("data-table-id", tableId);
        
        // Use cell borders only (no table border) to prevent double borders
        table.style.border = "none";
        table.style.borderCollapse = "separate";
        table.style.borderSpacing = "0";
        
        // Set default 100% width for new tables
        table.setAttribute("data-width-xs", "percent:100:left:0:");
        table.style.width = "100%";
        table.style.minWidth = "100%";
        table.style.maxWidth = "100%";
        table.style.tableLayout = "fixed";
        
        // Apply border to cells with smart border-width to prevent double borders between cells
        const rows = table.querySelectorAll("tr");
        rows.forEach((row, rowIndex) => {
          const cells = row.querySelectorAll("td, th");
          cells.forEach((cell, colIndex) => {
            const cellEl = cell as HTMLElement;
            
            // Set data-column-index for responsive column CSS to work
            cellEl.setAttribute("data-column-index", String(colIndex));
            
            cellEl.style.borderColor = tableBorderColor;
            cellEl.style.borderStyle = "solid";
            
            // Remove top border for non-first rows and left border for non-first columns
            // to prevent double borders where cells meet
            const topWidth = rowIndex === 0 ? tableBorderWidth : 0;
            const leftWidth = colIndex === 0 ? tableBorderWidth : 0;
            cellEl.style.borderWidth = `${topWidth}px ${tableBorderWidth}px ${tableBorderWidth}px ${leftWidth}px`;
          });
        });
      });

      // Ensure ALL tables have proper wrappers (including the newly inserted one)
      ensureAllTablesHaveWrappers(doc);
      
      // Clean up styles for ALL existing tables to prevent TipTap's automatic styles
      // from overriding responsive CSS (TipTap sets col widths from colwidth attributes)
      doc.querySelectorAll("table[data-table-id]").forEach((existingTable) => {
        const tableEl = existingTable as HTMLTableElement;
        
        // Clean up col width styles
        tableEl.querySelectorAll("col").forEach((col) => {
          const colEl = col as HTMLElement;
          colEl.style.removeProperty("min-width");
          colEl.style.removeProperty("width");
          colEl.style.removeProperty("max-width");
          const colStyle = colEl.getAttribute("style");
          if (!colStyle || colStyle.trim() === "") {
            colEl.removeAttribute("style");
          }
        });
        
        // Clean up table min-width that TipTap might have added
        // (except for tables that need pixel widths based on data-width-* attributes)
        const widthXs = tableEl.getAttribute("data-width-xs");
        if (widthXs && widthXs.startsWith("pixel:")) {
          // This table has pixel width - leave it alone or restore from data attribute
          const [, value] = widthXs.split(":");
          if (value) {
            tableEl.style.width = `${value}px`;
            tableEl.style.minWidth = `${value}px`;
            tableEl.style.maxWidth = `${value}px`;
          }
        } else {
          // Clean up any auto-added min-width for non-pixel tables
          tableEl.style.removeProperty("min-width");
        }
        
        // Remove w-full class if present (it interferes with responsive styles)
        const currentClass = tableEl.getAttribute("class") || "";
        if (currentClass.includes("w-full")) {
          tableEl.setAttribute("class", currentClass.replace(/\bw-full\b/g, "").replace(/\s+/g, " ").trim());
        }
      });

      let newHtml = doc.body.innerHTML;
      
      // Prepend preserved style tags (responsive font/weight/align styles)
      if (preservedStyleTags.length > 0) {
        newHtml = preservedStyleTags.join('') + newHtml;
      }
      
      // Ensure there's a paragraph before tables for cursor positioning
      // Add <p></p> before table/wrapper if not already present
      newHtml = newHtml.replace(/^(<div class="table-wrapper"|<table)/gm, '<p></p>$1');
      newHtml = newHtml.replace(/(<\/table>|<\/div>)(<div class="table-wrapper"|<table)/g, '$1<p></p>$2');
      // But avoid duplicate paragraphs
      newHtml = newHtml.replace(/<p>\s*<\/p>\s*<p>\s*<\/p>/g, '<p></p>');
      
      // Generate CSS for ALL tables in the document (including existing tables)
      // This is critical to preserve styles when adding a new table
      const allStyleTags = generateCSSForAllTables(newHtml);
      if (allStyleTags) {
        newHtml = newHtml + allStyleTags;
      }
      
      isInternalChange.current = true;
      editor.commands.setContent(newHtml, { emitUpdate: true, parseOptions: { preserveWhitespace: 'full' } });
      onChange?.(newHtml);
      isInternalChange.current = false;
    }, 100);
  }, [editor, onChange, isInternalChange]);

  // Delete table and regenerate CSS for remaining tables
  const deleteTable = useCallback(() => {
    if (!editor) return;

    // Delete the table using TipTap
    editor.chain().focus().deleteTable().run();

    // After deletion, regenerate CSS for remaining tables
    setTimeout(() => {
      const html = editor.getHTML();
      
      // Extract non-table style tags BEFORE parsing
      const { html: htmlWithoutStyles, styleTags: preservedStyleTags } = extractNonTableStyleTags(html);
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlWithoutStyles, "text/html");
      
      // Clean up styles for ALL remaining tables
      doc.querySelectorAll("table[data-table-id]").forEach((existingTable) => {
        const tableEl = existingTable as HTMLTableElement;
        
        // Clean up col width styles
        tableEl.querySelectorAll("col").forEach((col) => {
          const colEl = col as HTMLElement;
          colEl.style.removeProperty("min-width");
          colEl.style.removeProperty("width");
          colEl.style.removeProperty("max-width");
          const colStyle = colEl.getAttribute("style");
          if (!colStyle || colStyle.trim() === "") {
            colEl.removeAttribute("style");
          }
        });
        
        // Clean up table min-width / restore pixel widths
        const widthXs = tableEl.getAttribute("data-width-xs");
        if (widthXs && widthXs.startsWith("pixel:")) {
          const [, value] = widthXs.split(":");
          if (value) {
            tableEl.style.width = `${value}px`;
            tableEl.style.minWidth = `${value}px`;
            tableEl.style.maxWidth = `${value}px`;
          }
        } else {
          tableEl.style.removeProperty("min-width");
        }
        
        // Remove w-full class
        const currentClass = tableEl.getAttribute("class") || "";
        if (currentClass.includes("w-full")) {
          tableEl.setAttribute("class", currentClass.replace(/\bw-full\b/g, "").replace(/\s+/g, " ").trim());
        }
      });

      // Ensure all remaining tables have proper wrappers
      ensureAllTablesHaveWrappers(doc);

      let newHtml = doc.body.innerHTML;
      
      // Prepend preserved style tags
      if (preservedStyleTags.length > 0) {
        newHtml = preservedStyleTags.join('') + newHtml;
      }
      
      // Generate CSS for ALL remaining tables
      const allStyleTags = generateCSSForAllTables(newHtml);
      if (allStyleTags) {
        newHtml = newHtml + allStyleTags;
      }
      
      isInternalChange.current = true;
      editor.commands.setContent(newHtml, { emitUpdate: true, parseOptions: { preserveWhitespace: 'full' } });
      onChange?.(newHtml);
      isInternalChange.current = false;
    }, 100);
  }, [editor, onChange, isInternalChange]);

  return {
    applyTableSettings,
    applyColumnStyles,
    applyRowStyles,
    applyCellStyles,
    resetColumnWidths,
    insertTable,
    deleteTable,
  };
};

// Helper function to validate column widths
function validateColumnWidths(
  finalHtml: string,
  colIndex: number,
  allColumnSettings: Record<number, ColumnSettings>,
  columnSettings: ColumnSettings
) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(finalHtml, "text/html");
  const table = doc.querySelector("table");
  if (!table) return;

  const tableSettingsAttr = table.getAttribute("data-table-settings");
  const totalColumns = table.querySelectorAll("tr:first-child th, tr:first-child td").length;
  
  // Merge current column settings with all saved column settings
  const mergedColumnSettings = { ...allColumnSettings, [colIndex]: columnSettings };
  
  // Check each breakpoint for warnings
  const pixelWarnings: string[] = [];
  const percentWarnings: string[] = [];
  
  (Object.keys(BREAKPOINTS) as BreakpointKey[]).forEach((bpKey) => {
    let totalPercentage = 0;
    let hasPercentageColumn = false;
    let hasAutoColumn = false;
    let totalPixelWidth = 0;
    let hasPixelColumn = false;
    
    for (let i = 0; i < totalColumns; i++) {
      const colSettings = mergedColumnSettings[i];
      const colConfig = colSettings?.styles?.[bpKey];
      
      if (colConfig?.widthType === "percent" && colConfig?.widthValue) {
        totalPercentage += parseFloat(colConfig.widthValue);
        hasPercentageColumn = true;
      } else if (colConfig?.widthType === "pixel" && colConfig?.widthValue) {
        totalPixelWidth += parseInt(colConfig.widthValue, 10);
        hasPixelColumn = true;
      } else if (!colConfig?.widthType || !colConfig?.widthValue) {
        // No width specified - treat as flexible column
        hasAutoColumn = true;
      }
    }
    
    if (hasPercentageColumn && Math.abs(totalPercentage - 100) > 1 && !hasAutoColumn) {
      percentWarnings.push(`${bpKey.toUpperCase()}: ${totalPercentage}%`);
    }
    
    if (tableSettingsAttr) {
      try {
        const parsedTableSettings = JSON.parse(tableSettingsAttr);
        const tableConfig = parsedTableSettings.tableWidths?.[bpKey];
        
        if (tableConfig?.type === "pixel" && tableConfig?.value && hasPixelColumn) {
          const tableWidth = parseInt(tableConfig.value, 10);
          if (totalPixelWidth > tableWidth && !tableConfig.horizontalScroll) {
            pixelWarnings.push(`${bpKey.toUpperCase()}: Columns (${totalPixelWidth}px) > Table (${tableWidth}px)`);
          }
        }
      } catch {
        // Ignore parse errors
      }
    }
  });
  
  // Validation warnings removed - users can configure columns as needed
}

